import { supabase } from './supabase'

/**
 * Stars you earn by taking part — REC-47, migration 099. Set on #/admin/stars (Star One admin, under Administration since 9 Oct).
 *
 * ── THE ACTIVITIES ARE A FIXED LIST, AND ITS HOME IS THE DATABASE ───────────
 *
 * `app.star_earning_catalogue()` is the hard-coded list (David, 23 Sep: "hard
 * code the activities"). `star_earning_rules()` returns every entry in it, in
 * its order, so this file does NOT keep a second list of keys to iterate —
 * that would be a second answer to "which activities exist" (rule 11), and the
 * day a migration adds one the screen would silently not show it. What lives
 * here is COPY: the words for each key, the split REC-1 once made for the recognition categories (whose labels became data in 128).
 * An unknown key renders as itself rather than vanishing.
 *
 * ── NOTHING HERE IS PER PERSON ──────────────────────────────────────────────
 *
 * D10 / B22. Every number this file carries is an aggregate per activity —
 * stars paid, people who earned, people at the cap. There is no function to
 * ask who earned what, and there must not be one: a "top earners" list is the
 * leaderboard through a fourth door.
 *
 * ── THERE IS NO CASH FIGURE ─────────────────────────────────────────────────
 *
 * D13, as in stars.ts. The 1¢ guide is never rendered, here or anywhere.
 */

export type EarningRule = {
  ruleKey: string
  /** 'oneconetec' for the platform's own activities; anything else is a connector. */
  source: string
  periodKind: 'once_ever' | 'monthly'
  stars: number
  /** null for a once-ever activity, and ONLY then — the database refuses otherwise. */
  monthlyCap: number | null
  isEnabled: boolean
  updatedAt: string | null
  updatedByName: string | null
  awardedThisMonth: number
  peopleThisMonth: number
  peopleAtCap: number
  awardedAllTime: number
  recoveredAllTime: number
}

export type StarEconomy = {
  periodMonth: string
  /** Stars colleagues GAVE this month (the giving side, star_period). */
  fromColleagues: number
  /** Stars monthly activities paid this month, gross. */
  fromActivity: number
  /** Welcome grants paid this month. */
  fromWelcome: number
  /** Stars taken back from removed posts and comments this month. */
  recovered: number
  /** Awards that FAILED this month — a trigger caught the error and logged it. */
  failedAwards: number
}

export async function loadEarningRules(): Promise<EarningRule[]> {
  const { data, error } = await supabase.rpc('star_earning_rules')
  if (error) throw error
  return (data ?? []).map((r: any) => ({
    ruleKey: r.rule_key,
    source: r.source,
    periodKind: r.period_kind,
    stars: r.stars,
    monthlyCap: r.monthly_cap,
    isEnabled: r.is_enabled,
    updatedAt: r.updated_at,
    updatedByName: r.updated_by_name,
    awardedThisMonth: r.awarded_this_month,
    peopleThisMonth: r.people_this_month,
    peopleAtCap: r.people_at_cap,
    awardedAllTime: r.awarded_all_time,
    recoveredAllTime: r.recovered_all_time,
  }))
}

export async function loadStarEconomy(): Promise<StarEconomy> {
  const { data, error } = await supabase.rpc('star_economy_this_month')
  if (error) throw error
  const r = (data ?? [])[0]
  if (!r) throw new Error('star_economy_this_month returned no row')
  return {
    periodMonth: r.period_month,
    fromColleagues: r.from_colleagues,
    fromActivity: r.from_activity,
    fromWelcome: r.from_welcome,
    recovered: r.recovered,
    failedAwards: r.failed_awards,
  }
}

/**
 * A REPLACE (rule 9): every value, every time. `monthlyCap` is null for a
 * once-ever activity and a number for a monthly one; the server refuses the
 * other two combinations rather than guessing.
 */
export async function saveEarningRule(rule: {
  ruleKey: string
  stars: number
  monthlyCap: number | null
  isEnabled: boolean
}): Promise<void> {
  const { error } = await supabase.rpc('set_star_earning_rule', {
    p_rule_key: rule.ruleKey,
    p_stars: rule.stars,
    p_monthly_cap: rule.monthlyCap,
    p_is_enabled: rule.isEnabled,
  })
  if (error) throw error
}

/** Words for each hard-coded activity. The KEYS come from the database. */
export const ACTIVITY_COPY: Record<string, { label: string; when: string }> = {
  'signin.first': {
    label: 'First sign-in',
    when: 'Once, the first time somebody signs in to OneConetec.',
  },
  'post.published': {
    label: 'A post goes live',
    when: 'When a post is published to the feed. A post the content check holds back earns nothing unless a moderator releases it.',
  },
  'photo.contest_entry': {
    label: 'A photo entered in the photo contest',
    when: 'Each photo posted to the feed that the company may use (the marketing, sales and training use is on unless the poster opts out under the photo), once per photo, when its post goes live. Opting out of that use, or a moderator removing the post, takes the stars back; using it again pays nothing.',
  },
  'photo.used': {
    label: 'A photo used beyond the feed',
    when: 'OFF by default since 146 (David, 10 Oct: held for now). When on: The first time a photo is downloaded from the photo room, its uploader earns this — once per photo, however many times or by whom it is downloaded. Downloading your own photo pays nothing. It is not taken back if the photo is later unticked or removed: it was used while it was permitted.',
  },
  'photo.contest_win': {
    label: 'Winning the photo contest',
    when: 'Paid when a contest judge chooses the winners: each place pays what its contest set (one winner at this rate by default). The rate here is the default for first place; the monthly cap bounds what one person can win in a month.',
  },
  'question.published': {
    label: 'A question goes up on the board',
    when: 'When a Wiki One board question goes live. Private questions to a support lane never earn.',
  },
  'comment.posted': {
    label: 'A comment',
    when: 'A comment or reply on a post, a recognition or a board question.',
  },
  'question.answered': {
    label: 'An answer on the board',
    when: "A top-level reply on somebody else's board question. Replies under an answer count as comments.",
  },
  'one_question.answered': {
    label: 'Answering One Question',
    when: 'The first answer to each week\'s One Question. Changing it pays nothing; taking it back while the week is open, or a moderator removing it, takes the stars back.',
  },
  'support.ticket_thanks': {
    label: 'Stars for a support ticket',
    when: 'A Platform support holder marks a ticket a good idea or bug report (a tenth of this rate, 500 by default), an important one (half, 2,500) or a great idea or critical bug (the full rate, 5,000). Platform support only; never on your own ticket. A ticket can be raised later and pays only the difference; it never goes down and nothing is taken back. Past the monthly cap the level is still recorded but pays nothing.',
  },
  'bis.course_completed': {
    label: 'BIS Safety — course completed',
    when: 'A course completion reported by BIS Safety.',
  },
}

export function activityLabel(key: string): string {
  return ACTIVITY_COPY[key]?.label ?? key
}

/**
 * A rule whose source is not the platform itself needs a CONNECTOR, and none
 * is built yet (099 ships the door, not the thing that walks through it). The
 * screen says so on the row, because a switch that is on and pays nobody is
 * standing rule 4's worst shape: it reads as working.
 */
export const CONNECTED_SOURCES: ReadonlySet<string> = new Set(['oneconetec'])

export function needsConnector(rule: Pick<EarningRule, 'source'>): boolean {
  return !CONNECTED_SOURCES.has(rule.source)
}

export function sourceLabel(source: string): string {
  if (source === 'oneconetec') return 'OneConetec'
  if (source === 'bis_safety') return 'BIS Safety'
  return source
}

/**
 * The most one person can earn from activity in a month at these rates — the
 * number REC-47 said to watch against the 1,000 colleagues can give. Only
 * ENABLED monthly rules count; the welcome grant is once and is not a monthly
 * figure. Derived, never stored (rule 8).
 */
export function monthlyActivityCeiling(rules: EarningRule[]): number {
  return rules
    .filter((r) => r.isEnabled && r.stars > 0 && r.periodKind === 'monthly' && r.monthlyCap !== null)
    .reduce((sum, r) => sum + (r.monthlyCap ?? 0), 0)
}

/**
 * The Star One admin page's filters (David, 9 Oct — People and roles'
 * treatment). OR within a group, AND across groups, the roster's rule. An
 * empty group is no filter. Client-side because the read is the whole,
 * short catalogue.
 */
export type EarningFilters = {
  query: string
  /** `source` values: 'oneconetec', 'bis_safety', … */
  sources: string[]
  kinds: EarningRule['periodKind'][]
  states: ('on' | 'off')[]
}

export function matchesEarningFilters(rule: EarningRule, f: EarningFilters): boolean {
  const q = f.query.trim().toLocaleLowerCase()
  if (q) {
    const copy = ACTIVITY_COPY[rule.ruleKey]
    const hay = [rule.ruleKey, copy?.label, copy?.when, sourceLabel(rule.source)]
    if (!hay.some((v) => (v ?? '').toLocaleLowerCase().includes(q))) return false
  }
  if (f.sources.length && !f.sources.includes(rule.source)) return false
  if (f.kinds.length && !f.kinds.includes(rule.periodKind)) return false
  if (f.states.length && !f.states.includes(rule.isEnabled ? 'on' : 'off')) return false
  return true
}
